import React, { useState, useEffect } from "react";
import { Upload, Eye, Loader, CheckCircle, X, AlertCircle, FileText } from "lucide-react";
import apiService from "../config"; // Assuming apiService is available or imported from your config

const ViewProposalDocument = () => {
  const [proposalFile, setProposalFile] = useState(null);
  const [existingProposal, setExistingProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [showDocViewer, setShowDocViewer] = useState(false);
  const [docUrl, setDocUrl] = useState('');
  const [isPdf, setIsPdf] = useState(false);

  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);

  // Fetch project and proposal IDs from localStorage (similar to your existing code)
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return projectData.project_id || projectData.id;
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return project.project_id || project.id;
        } catch (e) {
          console.error('Error parsing selected project:', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return user.data?.project_id || user.project_id;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      return null;
    };

    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;
        } catch (e) {
          console.error('Error parsing proposal data:', e);
        }
      }
      return null;
    };

    const projId = getProjectInfo();
    const propId = getProposalInfo();
    
    setProjectId(projId);
    setProposalId(propId);
    
    console.log('Project ID:', projId, 'Proposal ID:', propId);
  }, []);

  // Fetch existing proposal document
  const fetchProposalDocument = async () => {
    if (!projectId || !proposalId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.getDocuments(projectId, proposalId, 'AllDocuments'); // Adjust 'ProposalDocument' as per your API type

      if (response.status === 200 && response.data) {
        const doc = Array.isArray(response.data) ? response.data[0] : response.data;
        if (doc?.upload_path || doc?.document) {
          setExistingProposal({
            document: doc.upload_path || doc.document,
            created_at: doc.created_at || new Date().toISOString(),
          });
        } else {
          setExistingProposal(null);
        }
      }
    } catch (err) {
      console.error("Proposal document fetch error:", err);
      setExistingProposal(null);
    }finally {
      setLoading(false);   // ← always stop loading
    }
  };

  useEffect(() => {
    if (projectId && proposalId) {
      fetchProposalDocument();
    }
  }, [projectId, proposalId]);

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('कृपया फक्त PDF किंवा Image फाइल निवडा');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('फाइल साइज 10MB पेक्षा कमी असावी');
      return;
    }

    setProposalFile(file);
    setError(null);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!proposalFile) {
      setError('कृपया फाइल निवडा');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);

      const formData = new FormData();
      formData.append('type', 'ProposalDocument'); // Adjust type as needed
      formData.append('project_id', projectId);
      formData.append('proposal_id', proposalId);
      formData.append('document', proposalFile);

      const result = await apiService.postAddUpdateOtherDocuments(formData); // Adjust API method if needed

      const isSuccess = 
        !result ||
        result.status === 200 ||
        result.status === 201 ||
        result.status === 'success' ||
        result.success === true ||
        result.data?.success === true ||
        result.data?.status === 200 ||
        result.data?.status === 201 ||
        result.message?.toLowerCase()?.includes('success') ||
        result.data?.message?.toLowerCase()?.includes('success') ||
        result.documents?.document;

      if (isSuccess) {
        setSuccess(true);
        setProposalFile(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        await fetchProposalDocument();
        setTimeout(() => setSuccess(false), 4000);
      } else {
        setError('डॉक्युमेंट अपलोड करताना त्रुटी');
      }
    } catch (err) {
      console.error(err);
      setError('डॉक्युमेंट अपलोड करताना त्रुटी');
    } finally {
      setUploading(false);
    }
  };

  // Handle view
  const handleView = (fileOrUrl) => {
    let url, isPdfType;

    if (fileOrUrl instanceof File) {
      url = URL.createObjectURL(fileOrUrl);
      isPdfType = fileOrUrl.type === 'application/pdf';
    } else {
      url = fileOrUrl;
      isPdfType = url.toLowerCase().includes('.pdf');
    }

    setDocUrl(url);
    setIsPdf(isPdfType);
    setShowDocViewer(true);
  };

  // Close viewer
  const closeViewer = () => {
    setShowDocViewer(false);
    if (docUrl.startsWith('blob:')) URL.revokeObjectURL(docUrl);
    setDocUrl('');
    setIsPdf(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
      {/* Success / Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-800">
          <CheckCircle className="w-5 h-5 mr-3" />
          <span className="font-medium">डॉक्युमेंट यशस्वीरित्या अपलोड झाले!</span>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-3" />
            <span className="font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)}>
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div className="space-y-5">
        {/* <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            अपलोड करा
          </label>
          <input
            id="proposal-file-input"
            type="file"
            accept="application/pdf,image/jpeg,image/jpg,image/png"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-600
                     file:mr-4 file:py-2.5 file:px-5
                     file:rounded-lg file:border-0
                     file:text-sm file:font-semibold
                     file:bg-indigo-600 file:text-white
                     hover:file:bg-indigo-700
                     border border-gray-300 rounded-lg cursor-pointer"
          />
          {proposalFile && (
            <p className="mt-2 text-sm text-green-600">
              निवडलेली फाइल: {proposalFile.name}
            </p>
          )}
        </div>

        {proposalFile && (
          <div className="flex gap-4">
            <button
              onClick={() => handleView(proposalFile)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
            >
              <Eye size={18} /> पूर्वावलोकन
            </button>

            <button
              onClick={handleUpload}
              disabled={uploading}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-white transition-colors ${
                uploading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" /> अपलोड होत आहे...
                </>
              ) : (
                <>
                  <Upload size={18} /> सबमिट करा
                </>
              )}
            </button>
          </div>
        )} */}

        {/* Existing Document */}
        <div className="space-y-5">
        {/* Document Status Area */}
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-3" />
            <p>दस्तऐवज तपासले जात आहे...</p>
          </div>
        ) : existingProposal ? (
          <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileText className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-800">सध्याचा अपलोड केलेला दस्तऐवज</p>
                  <p className="text-sm text-gray-600 mt-1">
                    अपलोड दिनांक: {new Date(existingProposal.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleView(existingProposal.document)}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Eye size={18} /> पाहा
              </button>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-1">
              No Document Available
            </h3>
            <p className="text-sm text-gray-500">
              सध्या कोणताही प्रस्ताव दस्तऐवज अपलोड केलेला नाही
            </p>
          </div>
        )}

        {/* ← You can put your commented-out upload section here if you want to allow new uploads */}
      </div>
      </div>

      {/* Document Viewer Modal */}
      {showDocViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-6xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
            <div className="flex justify-between items-center p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="text-2xl font-bold text-gray-800">डॉक्युमेंट पूर्वावलोकन</h3>
              <button
                onClick={closeViewer}
                className="w-12 h-12 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 text-2xl font-bold flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <div className="h-[calc(95vh-80px)] bg-gray-50 flex items-center justify-center">
              {isPdf ? (
                <iframe
                  src={docUrl}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={docUrl}
                  alt="Document"
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewProposalDocument;